import JSEncrypt from "jsencrypt";
import { PUBLIC_KEY_PEM } from "@/config/publicKey";

/**
 * 将 ArrayBuffer 转为 Base64 字符串
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000; // 防止栈溢出
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

/**
 * 混合加密：AES-GCM 加密数据，RSA 加密 AES 密钥
 * @param {object} data - 需要加密的对象（如 ELongReservationsPayReq）
 * @returns {Promise<{encryptedAesKey: string, nonce: string, encryptedData: string}>}
 */
export async function hybridEncrypt(data) {
  try {
    // 1. 生成 AES-256 密钥（32字节）
    const aesKey = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt"],
    );
    const rawKey = await crypto.subtle.exportKey("raw", aesKey);
    const aesKeyBase64 = arrayBufferToBase64(rawKey);

    // 2. 生成随机 nonce（12字节）
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const nonceBase64 = arrayBufferToBase64(nonce.buffer);

    // 3. 序列化数据对象
    const plaintext = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(plaintext);

    // 4. AES-GCM 加密
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce },
      aesKey,
      dataBytes,
    );
    // encryptedBuffer 包含 ciphertext + tag（最后16字节）
    const encryptedDataBase64 = arrayBufferToBase64(encryptedBuffer);

    // 5. 使用 RSA 公钥加密 AES 密钥（Base64 字符串形式）
    const rsa = new JSEncrypt();
    rsa.setPublicKey(PUBLIC_KEY_PEM);
    const encryptedAesKey = rsa.encrypt(aesKeyBase64);
    if (!encryptedAesKey) {
      throw new Error("RSA 加密 AES 密钥失败");
    }

    // 6. 返回加密结果
    return {
      encryptedAesKey,
      nonce: nonceBase64,
      encryptedData: encryptedDataBase64,
    };
  } catch (error) {
    console.error("混合加密失败：", error);
    throw error;
  }
}
